<template>
  <div class="item-sort-order">
    <slot />
    <div class="form-group">
      <label :for="`itemSortOrderSearch-${uniqueId}`">Search:</label>
      <input
        :id="`itemSortOrderSearch-${uniqueId}`"
        v-model="search"
        class="form-control form-control-sm"
        type="text"
      />
    </div>
    <!-- Actions -->
    <div class="row mb-2">
      <div class="col col-6 d-flex justify-content-between align-items-start">
        <button
          class="btn btn-sm btn-outline-secondary"
          type="button"
          @click="removeAll"
        >
          < Hide All
        </button>
        <button
          class="btn btn-sm btn-outline-secondary"
          type="button"
          @click="addAll"
        >
          Show All >
        </button>
      </div>
      <div class="col col-6">
        <div v-show="available.length == 0" class="form-group form-check">
          <input
            id="includeFuture"
            v-model="includeFuture"
            class="form-check-input"
            type="checkbox"
          />
          <label class="form-check-label" for="includeFuture">
            Default new to "Shown"
          </label>
        </div>
        
        <p class="font-weight-bold mb-1">Sort By:</p>
        <select
          v-model="sort"
          class="custom-select custom-select-sm"
          @change="doSort"
        >
          <option disabled value="">Sort by...</option>
          <option value="az">Name A-Z</option>
          <option value="za">Name Z-A</option>
        </select>
        
        <p class="font-weight-bold mb-1 mt-2">View Type:</p>
        <select
          v-model="view"
          class="custom-select custom-select-sm"
        >
          <option disabled value="">View type...</option>
          <option value="gallery">Gallery</option>
          <option value="list">List</option>
        </select>
      </div>
    </div>
    <!-- Lists -->
    <div class="row">
      <div class="col col-6">
        <p class="font-weight-bold mb-1">Hidden:</p>
        <draggable
          v-bind="dragOptions"
          class="item-sort-order__list"
          :list="available"
        >
          <template v-for="element in available">
            <!-- Changes to slot need to be repeated in selected list below -->
            <slot :item="element" name="tag" :show="shouldShow(element.name)">
              <div
                v-show="shouldShow(element.name)"
                :key="element.id"
                class="item-sort-order__item"
                v-html="element.name"
              />
            </slot>
          </template>
        </draggable>
      </div>
      <div class="col col-6">
        <p class="font-weight-bold mb-1">
          Shown:
          <span v-if="minimumShown" class="mb-1 text-muted font-size-sm">
            ({{ minimumShown }} minimum)
          </span>
        </p>
        <draggable
          v-model="selected"
          v-bind="dragOptions"
          class="item-sort-order__list"
          @end="onShownVenueDragEnd"
          @start="onShownVenueDragStart"
        >
          <template v-for="element in selected">
            <!-- Changes to slot need to be repeated in available list above -->
            <slot :item="element" name="tag" :show="shouldShow(element.name)">
              <div
                v-show="shouldShow(element.name)"
                :key="element.id"
                class="item-sort-order__item"
                v-html="element.name"
              />
            </slot>
          </template>
        </draggable>
      </div>
    </div>
    <slot name="after" />
  </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
  name: 'ItemSortOrder',
  components: {
    draggable
  },
  props: {
    items: {
      required: true
    },
    value: {
      required: true
    },
    selectedCount: {
      type: Number | String,
      required: true
    },
    idFormat: {
      default: false
    },
    minimumShown: {
      default: false
    },
    fakeDefaultNone: {
      default: false
    }
  },
  data() {
    return {
      dragOptions: {
        group: 'items',
        forceFallback: true
      },
      dragStartCount: null,
      localItems: [],
      search: '',
      sort: '',
      view: ''
    };
  },
  created() {
    this.localItems = [...this.items]; // track data inernally
    if (this.selectedCount !== -1 && this.selectedCount < this.minimumShown)
      this.selected = this.localItems.slice(0, this.minimumShown);
    // for old data so this matches how it will be actually be displayed
    if (this.selectedCount === '') {
      this.selected = this.localItems;
      this.includeFuture = -1;
    }
  },
  computed: {
    uniqueId() {
      return (Math.floor(Math.random() * 10000) + 10000)
        .toString()
        .substring(1);
    },
    computedSelectedCount() {
      if (this.selectedCount === undefined) {
        if (this.fakeDefaultNone) return 0;
        return -1;
      }
      return this.selectedCount;
    },
    includeFuture: {
      get() {
        if (this.computedSelectedCount === -1) return true;
        return false;
      },
      set(v) {
        if (v) this.$emit('update-count', -1);
        else {
          this.$emit('update-count', this.selected.length);
          this.selected = [...this.localItems];
        }
      }
    },
    available() {
      if (this.computedSelectedCount === -1) return [];
      return this.localItems.slice(this.computedSelectedCount);
    },
    selected: {
      get() {
        if (this.computedSelectedCount === -1) return this.localItems;
        return this.localItems.slice(0, this.computedSelectedCount);
      },
      set(v) {
        const unmoved = this.localItems.filter(o => !v.includes(o));
        this.localItems = v.concat(unmoved);

        // enforce minimum
        if (v.length < this.minimumShown)
          v = this.localItems.slice(0, this.minimumShown);

        // formats
        let formatted;
        if (this.idFormat === 'type')
          formatted = v.map(o => ({ type: o.type, type_id: Number(o.typeId) }));
        else formatted = v.map(o => Number(o.id));

        this.$emit('update-count', formatted.length);
        this.$emit('input', formatted);
      }
    }
  },
  methods: {
    shouldShow(name) {
      if (this.search == '') return true;
      if (name.toLowerCase().includes(this.search.toLowerCase())) return true;
      return false;
    },
    addAll() {
      this.selected = this.selected.concat(this.available);
      this.includeFuture = true;
    },
    removeAll() {
      this.selected = [];
    },
    doSort() {
      const currentCount = this.computedSelectedCount;
      switch (this.sort) {
        case 'az':
          this.selected = this.selected.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          break;
        case 'za':
          this.selected = this.selected.sort((a, b) =>
            b.name.localeCompare(a.name)
          );
          break;

        default:
          break;
      }
      if (currentCount === -1) {
        this.$emit('update-count', -1);
      }

      setTimeout(() => {
        this.sort = '';
      }, 1000);
    },
    onShownVenueDragEnd({ pullMode }) {
      if (!pullMode && this.dragStartCount === -1) {
        this.$emit('update-count', -1);
      }
      this.dragStartCount = null;
    },
    onShownVenueDragStart() {
      this.dragStartCount = this.computedSelectedCount;
    }
  }
};
</script>

<style lang="scss">
.item-sort-order {
  border: 1px solid #e1e1e1;
  border-radius: 0.25rem;
  padding: 1.25rem 5px;
  margin-bottom: 1.5rem;

  &__list {
    min-height: 300px;
    max-height: 33vh;
    overflow: auto;
    background: linear-gradient(#ffffff 33%, rgba(255, 255, 255, 0)),
      linear-gradient(rgba(255, 255, 255, 0), #ffffff 66%) 0 100%,
      radial-gradient(
        farthest-side at 50% 0,
        rgba(34, 34, 34, 0.5),
        rgba(0, 0, 0, 0)
      ),
      radial-gradient(
          farthest-side at 50% 100%,
          rgba(34, 34, 34, 0.5),
          rgba(0, 0, 0, 0)
        )
        0 100%;
    background-color: #ffffff;
    background-repeat: no-repeat;
    background-attachment: local, local, scroll, scroll;
    background-size: 100% 45px, 100% 45px, 100% 15px, 100% 15px;
  }

  &__item {
    cursor: move;
    border: 1px solid rgba(0, 0, 0, 0.125);
    padding: 10px;
    background-color: transparent;
    font-size: 14px;
    line-break: 1.1;

    &:first-child {
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
    }

    &:last-child {
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
    }
  }
}
</style>
